/**
 * Campaigns hook — create a bulk campaign and send it (each recipient is enqueued
 * through the existing per-channel sender). `resp.data` is the unwrapped payload.
 */

import { useState, useCallback } from 'react';
import { useHttpClient } from '@actschurch/ccms-sdk';
import { getApiUrl } from '../../../../config/ccms';
import { toast } from '../../../../components/ui/providers/AlertProvider';

export interface CampaignRecipientInput {
  toAddress: string;
  contactName?: string;
  /** Positional template variables ({{1}},{{2}},…) from the uploaded row. */
  variables?: string[];
}

export interface CreateCampaignRequest {
  name: string;
  description?: string;
  channelId: number;
  subject?: string;
  messageBody: string;
  /** Email only: JSON list of attachment refs ({fileId, fileName, contentType, size}). */
  attachmentsJson?: string;
  /** WhatsApp: an APPROVED template name (sends the template, not free text). */
  templateName?: string;
  templateLanguage?: string;
  templateId?: number;
  /** WhatsApp media header (public S3 URL) + type, and dynamic URL button param. */
  headerMediaUrl?: string;
  headerMediaType?: string;
  buttonUrlParam?: string;
  buttonIndex?: number;
  /** Bypass internal opt-out suppression. */
  includeOptedOut?: boolean;
  /**
   * Push only: the constellation appId (e.g. "ccms.task") the campaign targets. Selects the APNs
   * topic (bundle id) for every push it creates; without it iOS rejects them with
   * DeviceTokenNotForTopic.
   */
  appId?: string;
  /** Push only: public S3 URL of the notification image. */
  imageUrl?: string;
  /** Push only: "default", a bundled sound name, or "none" for a silent push. */
  sound?: string;
  /** If set (future, yyyyMMddHHmmss UTC), the campaign is scheduled, not sent now. */
  scheduledDateTime?: number;
  recipients: CampaignRecipientInput[];
}

export interface CampaignView {
  campaignId: number;
  name: string;
  channelId: number;
  statusId: number;
  description?: string | null;
  subject?: string | null;
  messageBody?: string | null;
  templateName?: string | null;
  templateLanguage?: string | null;
  totalRecipients: number;
  sentCount: number;
  deliveredCount: number;
  readCount: number;
  failedCount: number;
  createdDateTime?: number;
  scheduledDateTime?: number | null;
}

export interface CampaignListItem {
  campaignId: number;
  name: string;
  channelId: number;
  statusId: number;
  templateName?: string | null;
  totalRecipients: number;
  sentCount: number;
  failedCount: number;
  createdDateTime: number;
}

export interface CampaignRecipientView {
  recipientId: number;
  toAddress?: string | null;
  contactName?: string | null;
  statusId: number;
  messageId?: number | null;
  failureReason?: string | null;
  sentDateTime?: number | null;
}

const path = (p: string) => getApiUrl('communications', `/api/campaigns${p}`);

export function useCampaigns() {
  const httpClient = useHttpClient();
  const [submitting, setSubmitting] = useState(false);

  const createCampaign = useCallback(
    async (body: CreateCampaignRequest): Promise<CampaignView | null> => {
      const resp = await httpClient.post<CampaignView>(path(''), body);
      return (resp.data as CampaignView) ?? null;
    },
    [httpClient],
  );

  const sendCampaign = useCallback(
    async (id: number): Promise<CampaignView | null> => {
      const resp = await httpClient.post<CampaignView>(path(`/${id}/send`), {});
      return (resp.data as CampaignView) ?? null;
    },
    [httpClient],
  );

  /** Create + immediately send. Returns the final campaign (with counts) or null. */
  const createAndSend = useCallback(
    async (body: CreateCampaignRequest): Promise<CampaignView | null> => {
      setSubmitting(true);
      try {
        const created = await createCampaign(body);
        if (!created) {
          toast.error('Failed to create campaign');
          return null;
        }
        const sent = await sendCampaign(created.campaignId);
        toast.success(`Campaign sent: ${sent?.sentCount ?? 0} queued, ${sent?.failedCount ?? 0} failed`);
        return sent ?? created;
      } catch (err: any) {
        toast.error(err?.message || 'Failed to send campaign');
        return null;
      } finally {
        setSubmitting(false);
      }
    },
    [createCampaign, sendCampaign],
  );

  const listCampaigns = useCallback(
    async (channelId?: number): Promise<CampaignListItem[]> => {
      const resp = await httpClient.get<CampaignListItem[]>(path(''));
      const all = (resp.data as CampaignListItem[]) ?? [];
      return channelId === undefined ? all : all.filter((c) => c.channelId === channelId);
    },
    [httpClient],
  );

  const getCampaign = useCallback(
    async (id: number): Promise<CampaignView | null> => {
      const resp = await httpClient.get<CampaignView>(path(`/${id}`));
      return (resp.data as CampaignView) ?? null;
    },
    [httpClient],
  );

  const getRecipients = useCallback(
    async (id: number): Promise<CampaignRecipientView[]> => {
      const resp = await httpClient.get<CampaignRecipientView[]>(path(`/${id}/recipients`));
      return (resp.data as CampaignRecipientView[]) ?? [];
    },
    [httpClient],
  );

  const retryRecipient = useCallback(
    async (campaignId: number, recipientId: number): Promise<CampaignRecipientView | null> => {
      const resp = await httpClient.post<CampaignRecipientView>(path(`/${campaignId}/recipients/${recipientId}/retry`), {});
      return (resp.data as CampaignRecipientView) ?? null;
    },
    [httpClient],
  );

  return { submitting, createCampaign, sendCampaign, createAndSend, listCampaigns, getCampaign, getRecipients, retryRecipient };
}
